import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Toggle } from '../../components/ui/Toggle';
import { Search, ArrowLeft, Calendar, Clock, FileIcon, Send, MessageSquare, Building2, Lock, FileText, AlertCircle, CheckCircle, User } from 'lucide-react';
import { mockTickets, statusConfig, priorityConfig, adminTeam, SupportTicket, TicketStatus, TicketPriority } from '../../data/supportData';
export function AdminSupportPage() {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TicketStatus>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | TicketPriority>('all');
  const [builderFilter, setBuilderFilter] = useState('all');
  const [tickets, setTickets] = useState<SupportTicket[]>(mockTickets);
  // Reply Form State
  const [replyContent, setReplyContent] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const builders = useMemo(() => {
    const unique = new Map();
    tickets.forEach(t => unique.set(t.builderId, t.builderName));
    return Array.from(unique.entries()).map(([id, name]) => ({
      id,
      name
    }));
  }, [tickets]);
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) || ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) || ticket.builderName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
      const matchesBuilder = builderFilter === 'all' || ticket.builderId === builderFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesBuilder;
    });
  }, [tickets, searchQuery, statusFilter, priorityFilter, builderFilter]);
  const stats = useMemo(() => {
    return {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      inProgress: tickets.filter(t => t.status === 'in-progress').length,
      waiting: tickets.filter(t => t.status === 'waiting-on-builder').length,
      resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length
    };
  }, [tickets]);
  const handleViewTicket = (ticket: SupportTicket) => {
    if (ticket.unreadByAdmin) {
      setTickets(prev => prev.map(t => t.id === ticket.id ? {
        ...t,
        unreadByAdmin: false
      } : t));
      ticket.unreadByAdmin = false;
    }
    setSelectedTicket(ticket);
    setView('detail');
  };
  const handleBackToList = () => {
    setSelectedTicket(null);
    setView('list');
  };
  const handleStatusChange = (newStatus: TicketStatus) => {
    if (!selectedTicket) return;
    const updated = {
      ...selectedTicket,
      status: newStatus,
      updatedDate: new Date().toISOString()
    };
    setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updated : t));
    setSelectedTicket(updated);
  };
  const handleAssigneeChange = (newAssignee: string) => {
    if (!selectedTicket) return;
    const updated = {
      ...selectedTicket,
      assignedTo: newAssignee,
      updatedDate: new Date().toISOString()
    };
    setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updated : t));
    setSelectedTicket(updated);
  };
  const handleSendReply = () => {
    if (!replyContent.trim() || !selectedTicket) return;
    const newMessage = {
      id: `msg-${Date.now()}`,
      senderId: 'a-1',
      senderName: 'ResVR Admin',
      senderType: 'admin' as const,
      senderInitials: 'RA',
      content: replyContent,
      timestamp: new Date().toISOString(),
      isInternal: isInternalNote
    };
    const updatedTicket = {
      ...selectedTicket,
      updatedDate: new Date().toISOString(),
      unreadByBuilder: !isInternalNote,
      messages: [...selectedTicket.messages, newMessage]
    };
    setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updatedTicket : t));
    setSelectedTicket(updatedTicket);
    setReplyContent('');
    setIsInternalNote(false);
  };
  if (view === 'detail' && selectedTicket) {
    const StatusIcon = statusConfig[selectedTicket.status].icon;
    return <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-2333">
        <button onClick={handleBackToList} className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-2334">
          <ArrowLeft className="h-4 w-4 mr-1" data-id="element-2335" />
          Back to All Tickets
        </button>

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-2336">
          <div data-id="element-2337">
            <div className="flex items-center gap-3 mb-2" data-id="element-2338">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-2339">
                {selectedTicket.id} | {selectedTicket.title}
              </h1>
              <Badge variant={priorityConfig[selectedTicket.priority].variant} data-id="element-2340">
                {priorityConfig[selectedTicket.priority].label} Priority
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500" data-id="element-2341">
              <span className="flex items-center gap-1.5" data-id="element-2342">
                <Calendar className="h-4 w-4" data-id="element-2343" />
                Created{' '}
                {new Date(selectedTicket.createdDate).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-2344">
                <Clock className="h-4 w-4" data-id="element-2345" />
                Last activity:{' '}
                {new Date(selectedTicket.updatedDate).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-lg border border-slate-200" data-id="element-2346">
            <div className="flex flex-col" data-id="element-2347">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-2348">
                Status
              </label>
              <select value={selectedTicket.status} onChange={e => handleStatusChange(e.target.value as TicketStatus)} className="text-sm border-slate-300 rounded-md py-1.5 pl-3 pr-8 focus:ring-primary-500 focus:border-primary-500 bg-white" data-id="element-2349">
                {Object.entries(statusConfig).map(([key, config]) => <option key={key} value={key} data-id="element-2350">
                    {config.label}
                  </option>)}
              </select>
            </div>
            <div className="flex flex-col" data-id="element-2351">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-2352">
                Assignee
              </label>
              <select value={selectedTicket.assignedTo || 'Unassigned'} onChange={e => handleAssigneeChange(e.target.value)} className="text-sm border-slate-300 rounded-md py-1.5 pl-3 pr-8 focus:ring-primary-500 focus:border-primary-500 bg-white" data-id="element-2353">
                {adminTeam.map(member => <option key={member} value={member} data-id="element-2354">
                    {member}
                  </option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2355">
          <div className="md:col-span-2 space-y-6" data-id="element-2356">
            <Card data-id="element-2357">
              <CardHeader className="bg-slate-50 border-b border-slate-100 pb-4" data-id="element-2358">
                <CardTitle className="text-base" data-id="element-2359">Description</CardTitle>
              </CardHeader>
              <CardContent className="pt-6" data-id="element-2360">
                <div className="bg-slate-50 rounded-lg p-4 text-slate-700 whitespace-pre-wrap" data-id="element-2361">
                  {selectedTicket.description}
                </div>
              </CardContent>
            </Card>

            <Card data-id="element-2362">
              <CardContent className="p-0" data-id="element-2363">
                <div className="divide-y divide-slate-100" data-id="element-2364">
                  {selectedTicket.messages.map(msg => <div key={msg.id} className={`p-6 flex gap-4 ${msg.isInternal ? 'bg-amber-50/50' : ''}`} data-id="element-2365">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${msg.senderType === 'admin' ? 'bg-slate-800 text-white' : 'bg-primary-100 text-primary-700'}`} data-id="element-2366">
                        {msg.senderInitials}
                      </div>
                      <div className="flex-1 space-y-2" data-id="element-2367">
                        <div className="flex items-baseline justify-between" data-id="element-2368">
                          <div className="flex items-center gap-2" data-id="element-2369">
                            <span className="font-semibold text-slate-900" data-id="element-2370">
                              {msg.senderName}
                            </span>
                            {msg.isInternal && <Badge variant="warning" className="text-[10px] py-0 h-5" data-id="element-2371">
                                <Lock className="h-3 w-3 mr-1" data-id="element-2372" /> Internal Note
                              </Badge>}
                          </div>
                          <span className="text-xs text-slate-500" data-id="element-2373">
                            {new Date(msg.timestamp).toLocaleString([], {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                          </span>
                        </div>
                        <div className={`whitespace-pre-wrap ${msg.isInternal ? 'text-amber-900' : 'text-slate-700'}`} data-id="element-2374">
                          {msg.content}
                        </div>
                        {msg.attachments && msg.attachments.length > 0 && <div className="flex flex-wrap gap-2 pt-2" data-id="element-2375">
                            {msg.attachments.map((file, i) => <div key={i} className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-medium ${msg.isInternal ? 'bg-amber-100/50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-2376">
                                <FileIcon className="h-3.5 w-3.5 opacity-70" data-id="element-2377" />
                                {file.name}
                              </div>)}
                          </div>}
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            <Card data-id="element-2378">
              <CardHeader className="pb-4" data-id="element-2379">
                <CardTitle className="text-base" data-id="element-2380">Reply</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-2381">
                <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="Type your response or internal note here..." className={`w-full min-h-[120px] p-3 border rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-y transition-colors ${isInternalNote ? 'bg-amber-50 border-amber-300 placeholder-amber-700/50' : 'border-slate-300'}`} data-id="element-2382" />

                <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-2383">
                  <div className="flex items-center gap-3" data-id="element-2384">
                    <Toggle checked={isInternalNote} onChange={setIsInternalNote} label="" data-id="element-2385" />
                    <div data-id="element-2386">
                      <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5" data-id="element-2387">
                        <Lock className="h-4 w-4 text-amber-500" data-id="element-2388" /> Internal
                        Note
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-2389">
                        Only visible to ResVR admins
                      </p>
                    </div>
                  </div>
                  <Button onClick={handleSendReply} disabled={!replyContent.trim()} leftIcon={<Send className="h-4 w-4" data-id="element-2391" />} variant={isInternalNote ? 'secondary' : 'primary'} data-id="element-2390">
                    {isInternalNote ? 'Add Note' : 'Send to Builder'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6" data-id="element-2392">
            <Card data-id="element-2393">
              <CardHeader className="bg-slate-50 border-b border-slate-100 pb-4" data-id="element-2394">
                <CardTitle className="text-base" data-id="element-2395">Builder Info</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4" data-id="element-2396">
                <div className="flex items-center gap-3" data-id="element-2397">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-2398">
                    <Building2 className="h-5 w-5 text-slate-500" data-id="element-2399" />
                  </div>
                  <div data-id="element-2400">
                    <p className="font-semibold text-slate-900" data-id="element-2401">
                      {selectedTicket.builderName}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2402">
                      {selectedTicket.builderId}
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-100" data-id="element-2403">
                  <Button variant="outline" className="w-full" leftIcon={<User className="h-4 w-4" data-id="element-2405" />} data-id="element-2404">
                    View Builder Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>;
  }
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-2406">
      <div data-id="element-2407">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2408">Support Tickets</h1>
        <p className="text-slate-500" data-id="element-2409">
          Manage support requests from all builders
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-2410">
        <Card noPadding data-id="element-2411">
          <div className="p-4" data-id="element-2412">
            <div className="flex items-center justify-between mb-2" data-id="element-2413">
              <p className="text-sm text-slate-500" data-id="element-2414">Total Tickets</p>
              <FileText className="h-4 w-4 text-slate-400" data-id="element-2415" />
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-2416">{stats.total}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-2417">
          <div className="p-4" data-id="element-2418">
            <div className="flex items-center justify-between mb-2" data-id="element-2419">
              <p className="text-sm text-slate-500" data-id="element-2420">Open</p>
              <Clock className="h-4 w-4 text-amber-500" data-id="element-2421" />
            </div>
            <p className="text-2xl font-bold text-amber-600" data-id="element-2422">{stats.open}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-2423">
          <div className="p-4" data-id="element-2424">
            <div className="flex items-center justify-between mb-2" data-id="element-2425">
              <p className="text-sm text-slate-500" data-id="element-2426">In Progress</p>
              <AlertCircle className="h-4 w-4 text-blue-500" data-id="element-2427" />
            </div>
            <p className="text-2xl font-bold text-blue-600" data-id="element-2428">
              {stats.inProgress}
            </p>
          </div>
        </Card>
        <Card noPadding data-id="element-2429">
          <div className="p-4" data-id="element-2430">
            <div className="flex items-center justify-between mb-2" data-id="element-2431">
              <p className="text-sm text-slate-500" data-id="element-2432">Waiting on Builder</p>
              <Clock className="h-4 w-4 text-slate-400" data-id="element-2433" />
            </div>
            <p className="text-2xl font-bold text-slate-700" data-id="element-2434">{stats.waiting}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-2435">
          <div className="p-4" data-id="element-2436">
            <div className="flex items-center justify-between mb-2" data-id="element-2437">
              <p className="text-sm text-slate-500" data-id="element-2438">Resolved</p>
              <CheckCircle className="h-4 w-4 text-green-500" data-id="element-2439" />
            </div>
            <p className="text-2xl font-bold text-green-600" data-id="element-2440">
              {stats.resolved}
            </p>
          </div>
        </Card>
      </div>

      <Card noPadding data-id="element-2441">
        <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl flex flex-col lg:flex-row gap-3" data-id="element-2442">
          <div className="flex-1 relative" data-id="element-2443">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-2444" />
            <input type="text" placeholder="Search by ID, title, builder..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2445" />
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-2446">
            <select value={builderFilter} onChange={e => setBuilderFilter(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 min-w-[140px]" data-id="element-2447">
              <option value="all" data-id="element-2448">All Builders</option>
              {builders.map(b => <option key={b.id} value={b.id} data-id="element-2449">
                  {b.name}
                </option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 min-w-[140px]" data-id="element-2450">
              <option value="all" data-id="element-2451">All Status</option>
              {Object.entries(statusConfig).map(([key, config]) => <option key={key} value={key} data-id="element-2452">
                  {config.label}
                </option>)}
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 min-w-[140px]" data-id="element-2453">
              <option value="all" data-id="element-2454">All Priorities</option>
              {Object.entries(priorityConfig).map(([key, config]) => <option key={key} value={key} data-id="element-2455">
                  {config.label}
                </option>)}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-2456">
          <table className="w-full text-left text-sm" data-id="element-2457">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-2458">
              <tr data-id="element-2459">
                <th className="px-6 py-4" data-id="element-2460">ID</th>
                <th className="px-6 py-4" data-id="element-2461">Builder</th>
                <th className="px-6 py-4" data-id="element-2462">Title</th>
                <th className="px-6 py-4" data-id="element-2463">Status</th>
                <th className="px-6 py-4" data-id="element-2464">Priority</th>
                <th className="px-6 py-4" data-id="element-2465">Assigned To</th>
                <th className="px-6 py-4" data-id="element-2466">Updated</th>
                <th className="px-6 py-4 text-right" data-id="element-2467">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2468">
              {filteredTickets.length === 0 ? <tr data-id="element-2469">
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-500" data-id="element-2470">
                    <MessageSquare className="h-8 w-8 mx-auto mb-3 text-slate-300" data-id="element-2471" />
                    <p data-id="element-2472">No tickets found matching your filters.</p>
                  </td>
                </tr> : filteredTickets.map(ticket => {
              const StatusIcon = statusConfig[ticket.status].icon;
              return <tr key={ticket.id} className="hover:bg-slate-50 transition-colors" data-id="element-2473">
                      <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2474">
                        <div className="flex items-center gap-2" data-id="element-2475">
                          {ticket.unreadByAdmin && <span className="h-2 w-2 rounded-full bg-primary-500" data-id="element-2476"></span>}
                          {ticket.id}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2477">
                        <div className="flex items-center gap-2" data-id="element-2478">
                          <Building2 className="h-4 w-4 text-slate-400" data-id="element-2479" />
                          <span className="text-slate-700" data-id="element-2480">
                            {ticket.builderName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-900 max-w-xs truncate" title={ticket.title} data-id="element-2481">
                        {ticket.title}
                      </td>
                      <td className="px-6 py-4" data-id="element-2482">
                        <Badge variant={statusConfig[ticket.status].variant} data-id="element-2483">
                          <div className="flex items-center gap-1" data-id="element-2484">
                            <StatusIcon className="h-3 w-3" data-id="element-2485" />
                            {statusConfig[ticket.status].label}
                          </div>
                        </Badge>
                      </td>
                      <td className="px-6 py-4" data-id="element-2486">
                        <Badge variant={priorityConfig[ticket.priority].variant} data-id="element-2487">
                          {priorityConfig[ticket.priority].label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-2488">
                        {ticket.assignedTo || <span className="text-slate-400 italic" data-id="element-2489">
                            Unassigned
                          </span>}
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-2490">
                        {new Date(ticket.updatedDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2491">
                        <Button variant="ghost" size="sm" onClick={() => handleViewTicket(ticket)} data-id="element-2492">
                          View
                        </Button>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        {filteredTickets.length > 0 && <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50 rounded-b-xl" data-id="element-2493">
            <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-2494">
              <span data-id="element-2495">Items per page:</span>
              <select className="border-slate-300 rounded-md py-1 pl-2 pr-6 text-sm" data-id="element-2496">
                <option data-id="element-2497">10</option>
                <option data-id="element-2498">25</option>
                <option data-id="element-2499">50</option>
              </select>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-2500">
              <span data-id="element-2501">
                1 - {filteredTickets.length} of {filteredTickets.length}
              </span>
              <div className="flex gap-1" data-id="element-2502">
                <Button variant="ghost" size="sm" disabled className="px-2" data-id="element-2503">
                  &lt;
                </Button>
                <Button variant="ghost" size="sm" disabled className="px-2" data-id="element-2504">
                  &gt;
                </Button>
              </div>
            </div>
          </div>}
      </Card>
    </div>;
}